/**
 * Booking Service
 * Handles creation and management of bookings from trip planner suggestions
 */

import { supabase } from '../lib/supabase';

/**
 * Creates a booking from a hotel suggestion
 * @param {Object} hotel - Hotel object from trip planner
 * @param {Object} tripDetails - Trip details (dates, guests)
 * @returns {Object} Booking object
 */
export function createHotelBooking(hotel, tripDetails) {
  const bookingId = `BK${Date.now()?.toString()?.slice(-6)}`;
  const checkInDate = tripDetails?.startDate || new Date()?.toISOString()?.split('T')?.[0];
  const checkOutDate = tripDetails?.endDate || new Date(Date.now() + 5 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];
  
  return {
    id: bookingId,
    type: 'accommodation',
    name: hotel?.name,
    location: hotel?.location,
    description: hotel?.description,
    image: hotel?.image || 'https://images.unsplash.com/photo-1566073771259-6a8506099945',
    imageAlt: `${hotel?.name} - ${hotel?.category} accommodation in ${hotel?.location}`,
    checkIn: checkInDate,
    checkOut: checkOutDate,
    guests: tripDetails?.travelers || 2,
    amount: hotel?.totalCost,
    paymentStatus: 'pending',
    bookingStatus: 'confirmed',
    confirmationNumber: `CONF-${new Date()?.getFullYear()}-${bookingId}`,
    paymentMethod: 'pending',
    dueDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
    cancellationPolicy: 'Free cancellation until 7 days before check-in. 50% refund for cancellations 3-7 days before. No refund for cancellations within 3 days.',
    refundPolicy: 'Full refund processed within 7-10 business days to original payment method',
    contactEmail: 'reservations@example.ph',
    contactPhone: '+63 917 123 4567',
    amenities: hotel?.amenities || [],
    category: hotel?.category,
    rating: hotel?.rating
  };
}

/**
 * Creates a booking from a restaurant/activity suggestion
 * @param {Object} restaurant - Restaurant object from trip planner
 * @param {Object} tripDetails - Trip details (dates, guests)
 * @returns {Object} Booking object
 */
export function createActivityBooking(restaurant, tripDetails) {
  const bookingId = `BK${Date.now()?.toString()?.slice(-6)}`;
  const activityDate = tripDetails?.startDate || new Date()?.toISOString()?.split('T')?.[0];
  
  return {
    id: bookingId,
    type: 'activity',
    name: restaurant?.name,
    location: restaurant?.location,
    description: restaurant?.description,
    image: restaurant?.image || 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0',
    imageAlt: `${restaurant?.name} - ${restaurant?.cuisine} dining experience in ${restaurant?.location}`,
    checkIn: activityDate,
    checkOut: activityDate,
    guests: tripDetails?.travelers || 2,
    amount: restaurant?.totalCost,
    paymentStatus: 'pending',
    bookingStatus: 'confirmed',
    confirmationNumber: `CONF-${new Date()?.getFullYear()}-${bookingId}`,
    paymentMethod: 'pending',
    dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
    cancellationPolicy: 'Free cancellation until 24 hours before activity. No refund for cancellations within 24 hours.',
    refundPolicy: 'Full refund processed within 3-5 business days',
    contactEmail: 'reservations@example.ph',
    contactPhone: '+63 918 234 5678',
    cuisine: restaurant?.cuisine,
    rating: restaurant?.rating,
    mealType: restaurant?.mealType,
    specialties: restaurant?.specialties || []
  };
}

/**
 * Saves booking to Supabase database
 * @param {Object} booking - Booking object to save
 * @param {string} userId - User ID
 */
export async function saveBooking(booking, userId) {
  try {
    if (!userId) {
      throw new Error('User must be authenticated to save bookings');
    }

    const { data, error } = await supabase?.from('bookings')?.insert({
        user_id: userId,
        booking_id: booking?.id,
        confirmation_number: booking?.confirmationNumber,
        type: booking?.type,
        name: booking?.name,
        location: booking?.location,
        description: booking?.description,
        image: booking?.image,
        image_alt: booking?.imageAlt,
        check_in: booking?.checkIn,
        check_out: booking?.checkOut,
        guests: booking?.guests,
        amount: booking?.amount,
        payment_status: booking?.paymentStatus,
        booking_status: booking?.bookingStatus,
        payment_method: booking?.paymentMethod,
        due_date: booking?.dueDate,
        cancellation_policy: booking?.cancellationPolicy,
        refund_policy: booking?.refundPolicy,
        contact_email: booking?.contactEmail,
        contact_phone: booking?.contactPhone
      })?.select()?.single();

    if (error) throw error;

    return { success: true, booking: convertBookingFromDb(data) };
  } catch (error) {
    console.error('Error saving booking:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Retrieves all bookings from Supabase for the current user
 * @param {string} userId - User ID
 * @returns {Array} Array of booking objects
 */
export async function getBookings(userId) {
  try {
    if (!userId) {
      return [];
    }

    const { data, error } = await supabase?.from('bookings')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(convertBookingFromDb) || [];
  } catch (error) {
    console.error('Error retrieving bookings:', error);
    return [];
  }
}

/**
 * Clears all bookings from localStorage (for testing)
 */
export function clearBookings() {
  try {
    localStorage.removeItem('userBookings');
    return { success: true };
  } catch (error) {
    console.error('Error clearing bookings:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Convert booking from database format (snake_case) to app format (camelCase)
 */
function convertBookingFromDb(booking) {
  if (!booking) return null;

  return {
    id: booking?.booking_id,
    dbId: booking?.id,
    type: booking?.type,
    name: booking?.name,
    location: booking?.location,
    description: booking?.description,
    image: booking?.image,
    imageAlt: booking?.image_alt,
    checkIn: booking?.check_in,
    checkOut: booking?.check_out,
    guests: booking?.guests,
    amount: parseFloat(booking?.amount),
    paymentStatus: booking?.payment_status,
    bookingStatus: booking?.booking_status,
    confirmationNumber: booking?.confirmation_number,
    paymentMethod: booking?.payment_method,
    dueDate: booking?.due_date,
    cancellationPolicy: booking?.cancellation_policy,
    refundPolicy: booking?.refund_policy,
    contactEmail: booking?.contact_email,
    contactPhone: booking?.contact_phone,
    createdAt: booking?.created_at,
    updatedAt: booking?.updated_at
  };
}